import { State, Sub, Pub } from '../../../../shared/state/state.abstract.ts';
import {
  merge,
  Observable,
  map,
  BehaviorSubject,
  share,
  tap,
  switchMap,
  filter,
  zipWith,
  take,
} from 'rxjs';
import { ITabState } from './tabState.model.ts';
import { GridSortModel } from '@mui/x-data-grid/models/gridSortModel';
import { dataHelper } from '../../../../shared/helpers/data/data.helper.ts';
import {
  bakery2bService,
  ListBakery2bQueryRes,
  SaveBakery2bClientMutationRes,
  DeactivateBakery2bClientMutationRes,
  ActivateBakery2bClientMutationRes,
  ReinviteBakery2bClientMutationRes,
  DeleteBakery2bClientMutationRes,
  ListCustomerConnectedWithBakery2bClientQueryRes,
  AddCustomerConnectedWithBakery2BClientMutationRes,
  DeleteCustomerConnectedWithBakery2BClientMutationRes,
} from '../services/bakery2b.service.ts';
import { responseHandler } from '../../../../shared/responseHandler/responseHandler.ts';
import {
  C_Save_Operation_Status,
  C_Deactivate_Operation_Status,
  C_Activate_Operation_Status,
  C_Invite_Operation_Status,
  C_Delete_Operation_Status,
  C_Global_Error_Code,
} from '../../../../graphql/generatedModel.ts';
import { snackbarService } from '../../../../shared/components/snackbar/service/snackbar.service.ts';
import i18n, { t } from 'i18next';
import { customerService, ListCustomersRes } from '../../../customer/services/customer.service.ts';
import { ISelectOption } from '../../../../shared/components/form/fields/select.component.tsx';
import { storageHelper } from '../../../../shared/helpers/storage';

export const initBakery2bClientsState: IBakery2bClientsState = {
  action: undefined,
  listBakery2bClient: [],
  allBakery2bClientListLength: 0,
  search: '',
  listBakery2bClientSortModel: [{ field: 'email', sort: 'asc' }],
  selectedBakery2bClient: null,
  customerListConnectedWithBakery2BClient: [],
  listGridCustomers: [],
  customerConnectedWithBakery2BClientProcessedId: null,
};

class PubImpl extends Pub<IBakery2bClientsState> {
  init(state: Partial<IBakery2bClientsState>): void {
    this.emit('init', { ...state });
  }
  search(search: string): void {
    this.emit('search', { search });
  }
  sort(listBakery2bClientSortModel: IBakery2bClientsState['listBakery2bClientSortModel']): void {
    this.emit('sort', { listBakery2bClientSortModel });
  }
  bakery2bClientList(): void {
    this.emit('bakery2bClientList', {});
  }
  selectBakery2bClient(
    selectedBakery2bClient: IBakery2bClientsState['selectedBakery2bClient'],
  ): void {
    this.emit('selectBakery2bClient', { selectedBakery2bClient });
  }
  saveBakery2bClient(
    selectedBakery2bClient: IBakery2bClientsState['selectedBakery2bClient'],
  ): void {
    this.emit('saveBakery2bClient', { selectedBakery2bClient });
  }
  deactivateBakery2bClient(whyDeactivated: string): void {
    const updatedSelectedBakery2bClient = this.stream$.getValue().selectedBakery2bClient;
    this.emit('deactivateBakery2bClient', {
      selectedBakery2bClient: {
        ...updatedSelectedBakery2bClient,
        isDeactivated: true,
        whyDeactivated,
      },
    });
  }
  activateBakery2bClient(): void {
    const updatedSelectedBakery2bClient = this.stream$.getValue().selectedBakery2bClient;
    this.emit('activateBakery2bClient', {
      selectedBakery2bClient: {
        ...updatedSelectedBakery2bClient,
        isDeactivated: false,
        whyDeactivated: null,
      },
    });
  }
  reinviteBakery2bClient(): void {
    this.emit('reinviteBakery2bClient', {});
  }
  deleteBakery2bClient(): void {
    this.emit('deleteBakery2bClient', {});
  }
  customerListConnectedWithBakery2BClient(): void {
    this.emit('customerListConnectedWithBakery2BClient', {});
  }
  addCustomerConnectedWithBakery2BClient(
    customerConnectedWithBakery2BClientProcessedId: IBakery2bClientsState['customerConnectedWithBakery2BClientProcessedId'],
  ): void {
    this.emit('addCustomerConnectedWithBakery2BClient', {
      customerConnectedWithBakery2BClientProcessedId,
    });
  }
  deleteCustomerConnectedWithBakery2BClient(
    customerConnectedWithBakery2BClientProcessedId: IBakery2bClientsState['customerConnectedWithBakery2BClientProcessedId'],
  ): void {
    this.emit('deleteCustomerConnectedWithBakery2BClient', {
      customerConnectedWithBakery2BClientProcessedId,
    });
  }
}
class SubImpl extends Sub<IBakery2bClientsState> {
  private gridLoading$ = new BehaviorSubject<boolean>(false);
  private shareGridLoading$: Observable<boolean> = this.gridLoading$.pipe(share());
  private clientDetailsLoading$ = new BehaviorSubject<boolean>(false);
  private shareClientDetailsLoading$: Observable<boolean> = this.clientDetailsLoading$.pipe(
    share(),
  );
  private reinviteProcess$ = new BehaviorSubject<boolean>(false);
  private shareReinviteProcess$: Observable<boolean> = this.reinviteProcess$.pipe(share());
  private connectedCustomersLoading$ = new BehaviorSubject<boolean>(false);
  private shareConnectedCustomersLoading$: Observable<boolean> =
    this.connectedCustomersLoading$.pipe(share());
  protected actionHandlers(): Observable<IBakery2bClientsState> {
    return merge(
      this.updateState(),
      this.bakery2bClientList(),
      this.saveBakery2bClient(),
      this.deactivateBakery2bClient(),
      this.activateBakery2bClient(),
      this.reinviteBakery2bClient(),
      this.deleteBakery2bClient(),
      this.customerListConnectedWithBakery2BClient(),
      this.addCustomerConnectedWithBakery2BClient(),
      this.deleteCustomerConnectedWithBakery2BClient(),
    ).pipe(
      map(
        ({
          listBakery2bClient,
          search,
          listBakery2bClientSortModel,
          allBakery2bClientListLength,
          ...rest
        }) => {
          let sortedBakery2bClientList = structuredClone(listBakery2bClient);
          allBakery2bClientListLength = listBakery2bClient.length;
          listBakery2bClient = dataHelper
            .data(listBakery2bClient as [])
            .sort({
              sortModel: listBakery2bClientSortModel,
              callback: (data) => {
                sortedBakery2bClientList = data as [];
              },
            })
            .search({ search, fields: ['email', 'name', 'note'] })
            .result() as IBakery2bClientsState['listBakery2bClient'];
          const updatedState = {
            ...rest,
            listBakery2bClient,
            allBakery2bClientListLength,
            search,
            listBakery2bClientSortModel,
          };
          this.stream$.next({
            ...updatedState,
            listBakery2bClient: sortedBakery2bClientList,
            action: 'internalUpdate',
          });
          return updatedState;
        },
      ),
    );
  }
  gridLoading(): Observable<boolean> {
    return this.shareGridLoading$;
  }
  clientDetailsLoading(): Observable<boolean> {
    return this.shareClientDetailsLoading$;
  }
  reinviteProcess(): Observable<boolean> {
    return this.shareReinviteProcess$;
  }
  connectedCustomersLoading(): Observable<boolean> {
    return this.shareConnectedCustomersLoading$;
  }
  private bakery2bClientList(): Observable<IBakery2bClientsState> {
    return this.actionListener('bakery2bClientList').pipe(
      tap(() => {
        this.gridLoading$.next(true);
        bakery2bService.pub.listBakery2bClient();
      }),
      switchMap((state) => {
        return bakery2bService.sub.listBakery2bClient().pipe(
          responseHandler<ListBakery2bQueryRes>({
            errorReturnType: [],
          }),
          map((listBakery2bClient) => ({ ...state, listBakery2bClient })),
          tap(() => this.gridLoading$.next(false)),
        );
      }),
    );
  }
  private saveBakery2bClient(): Observable<IBakery2bClientsState> {
    return this.actionListener('saveBakery2bClient').pipe(
      filter(({ selectedBakery2bClient }) => !!selectedBakery2bClient),
      tap(({ selectedBakery2bClient }) => {
        bakery2bService.pub.saveBakery2bClient({
          dataToSave: {
            id: selectedBakery2bClient!.id,
            note: selectedBakery2bClient!.note,
            name: selectedBakery2bClient!.name,
            orderAddNewItem: selectedBakery2bClient!.orderAddNewItem,
            orderShowPrice: selectedBakery2bClient!.orderShowPrice,
            orderNoDelivery: selectedBakery2bClient!.orderNoDelivery,
            standardOrderEdit: selectedBakery2bClient!.standardOrderEdit,
          },
        });
        snackbarService.pub.show({
          id: 'savingBakery2bClient',
          content: i18n.t('common.saving_in_progress'),
          type: 'loading',
          noAutoHide: true,
        });
      }),
      switchMap(() => {
        return bakery2bService.sub.saveBakery2bClient().pipe(
          responseHandler<SaveBakery2bClientMutationRes>({
            errorReturnType: {
              status: C_Save_Operation_Status.SOS0_DATA_UNCHANGED,
            },
            quite: true,
          }),
        );
      }),
      map(({ status }) => {
        const updatedState = structuredClone(this.stream$.getValue());
        snackbarService.pub.hide('savingBakery2bClient');
        if (status === C_Save_Operation_Status.SOS1_DATA_CHANGED) {
          snackbarService.pub.show({
            type: 'success',
            content: t('settings.configuration_saved'),
          });
          updatedState.listBakery2bClient = updatedState.listBakery2bClient.map((el) => {
            if (el.id === updatedState.selectedBakery2bClient?.id) {
              return updatedState.selectedBakery2bClient;
            } else return el;
          });
        } else {
          snackbarService.pub.show({
            type: 'error',
            content: t('settings.configuration_not_saved'),
          });
        }
        return updatedState;
      }),
    );
  }
  private deactivateBakery2bClient(): Observable<IBakery2bClientsState> {
    return this.actionListener('deactivateBakery2bClient').pipe(
      filter(({ selectedBakery2bClient }) => !!selectedBakery2bClient),
      tap(({ selectedBakery2bClient }) => {
        this.clientDetailsLoading$.next(true);
        bakery2bService.pub.deactivateBakery2bClient({
          deactivateWaBakery2BClientId: selectedBakery2bClient!.id,
          whyDeactivated: selectedBakery2bClient!.whyDeactivated as string,
        });
      }),
      switchMap(() => {
        return bakery2bService.sub.deactivateBakery2bClient().pipe(
          responseHandler<DeactivateBakery2bClientMutationRes>({
            errorReturnType: {
              status: C_Deactivate_Operation_Status.DOS_RECORD_NOT_DEACTIVATED,
            },
            quite: true,
          }),
        );
      }),
      map(({ status }) => {
        const updatedState = structuredClone(this.stream$.getValue());
        if (status === C_Deactivate_Operation_Status.DOS_RECORD_DEACTIVATED) {
          snackbarService.pub.show({
            type: 'success',
            content: t('settings.deactivated_success'),
          });
          updatedState.listBakery2bClient = updatedState.listBakery2bClient.map((el) => {
            if (el.id === updatedState.selectedBakery2bClient?.id) {
              return updatedState.selectedBakery2bClient;
            } else return el;
          });
        } else {
          snackbarService.pub.show({
            type: 'error',
            content: t('settings.deactivation_failed'),
          });
          updatedState.action = undefined;
        }
        this.clientDetailsLoading$.next(false);
        return updatedState;
      }),
    );
  }
  private activateBakery2bClient(): Observable<IBakery2bClientsState> {
    return this.actionListener('activateBakery2bClient').pipe(
      filter(({ selectedBakery2bClient }) => !!selectedBakery2bClient),
      tap(({ selectedBakery2bClient }) => {
        this.clientDetailsLoading$.next(true);
        bakery2bService.pub.activateBakery2bClient({
          activateWaBakery2BClientId: selectedBakery2bClient!.id,
        });
      }),
      switchMap(() => {
        return bakery2bService.sub.activateBakery2bClient().pipe(
          responseHandler<ActivateBakery2bClientMutationRes>({
            errorReturnType: {
              status: C_Activate_Operation_Status.AOS_RECORD_NOT_ACTIVATED,
            },
            quite: true,
          }),
        );
      }),
      map(({ status }) => {
        const updatedState = structuredClone(this.stream$.getValue());
        if (status === C_Activate_Operation_Status.AOS_RECORD_ACTIVATED) {
          snackbarService.pub.show({
            type: 'success',
            content: t('settings.activated_success'),
          });
          updatedState.listBakery2bClient = updatedState.listBakery2bClient.map((el) => {
            if (el.id === updatedState.selectedBakery2bClient?.id) {
              return updatedState.selectedBakery2bClient;
            } else return el;
          });
        } else {
          snackbarService.pub.show({
            type: 'error',
            content: t('settings.activation_failed'),
          });
          updatedState.action = undefined;
        }
        this.clientDetailsLoading$.next(false);
        return updatedState;
      }),
    );
  }
  private reinviteBakery2bClient(): Observable<IBakery2bClientsState> {
    return this.actionListener('reinviteBakery2bClient').pipe(
      filter(({ selectedBakery2bClient }) => !!selectedBakery2bClient),
      tap(({ selectedBakery2bClient }) => {
        bakery2bService.pub.reinviteBakery2bClient({
          email: selectedBakery2bClient!.email,
          name: selectedBakery2bClient!.name,
        });
        this.reinviteProcess$.next(true);
      }),
      switchMap(() => {
        return bakery2bService.sub.reinviteBakery2bClient().pipe(
          responseHandler<ReinviteBakery2bClientMutationRes>({
            errorReturnType: {
              status: C_Invite_Operation_Status.IOS_INVITE_NOT_SENT,
            },
          }),
        );
      }),
      map(({ status }) => {
        const updatedState = structuredClone(this.stream$.getValue());
        if (status === C_Invite_Operation_Status.IOS_INVITE_SENT) {
          snackbarService.pub.show({
            type: 'success',
            content: t('settings.invitation_sent', {
              email: updatedState.selectedBakery2bClient!.email,
            }),
          });
        }
        this.reinviteProcess$.next(false);
        return updatedState;
      }),
      filter(() => false),
    );
  }
  private deleteBakery2bClient(): Observable<IBakery2bClientsState> {
    return this.actionListener('deleteBakery2bClient').pipe(
      filter(({ selectedBakery2bClient }) => !!selectedBakery2bClient),
      tap(({ selectedBakery2bClient }) => {
        bakery2bService.pub.deleteBakery2bClient({
          deleteWaBakery2BClientId: selectedBakery2bClient!.id,
        });
        snackbarService.pub.show({
          id: 'deletingBakery2bClient',
          content: i18n.t('common.deleting_in_progress'),
          type: 'loading',
          noAutoHide: true,
        });
      }),
      switchMap(() => {
        return bakery2bService.sub.deleteBakery2bClient().pipe(
          responseHandler<DeleteBakery2bClientMutationRes>({
            customErrorHandler: (err) => {
              snackbarService.pub.hide('deletingBakery2bClient');
              if (err.code === C_Global_Error_Code.GEC44_DELETE_USER_HAS_CUSTOMERS) {
                snackbarService.pub.show({
                  content: t(`errors.${C_Global_Error_Code.GEC44_DELETE_USER_HAS_CUSTOMERS}`),
                  type: 'warning',
                });
              } else {
                snackbarService.pub.show({
                  type: 'error',
                  content: t('common.delete_failed'),
                });
              }
              return {
                reportToSentry: err.code !== C_Global_Error_Code.GEC44_DELETE_USER_HAS_CUSTOMERS,
              };
            },
            quite: true,
            errorReturnType: {
              status: C_Delete_Operation_Status.DOS_RECORD_NOT_DELETED,
            },
          }),
        );
      }),
      filter(({ status }) => status === C_Delete_Operation_Status.DOS_RECORD_DELETED),
      map(() => {
        const updatedState = structuredClone(this.stream$.getValue());
        snackbarService.pub.hide('deletingBakery2bClient');
        snackbarService.pub.show({
          type: 'success',
          content: t('common.delete_success'),
        });
        storageHelper.memory.removeItem('configsData.dictBakery2bAccounts');
        updatedState.listBakery2bClient = updatedState.listBakery2bClient.filter(
          (el) => el.id !== updatedState.selectedBakery2bClient?.id,
        );
        updatedState.selectedBakery2bClient = null;
        return updatedState;
      }),
    );
  }
  private customerListConnectedWithBakery2BClient(): Observable<IBakery2bClientsState> {
    return this.actionListener('customerListConnectedWithBakery2BClient').pipe(
      filter(({ selectedBakery2bClient }) => !!selectedBakery2bClient),
      switchMap(async ({ selectedBakery2bClient }) => {
        this.connectedCustomersLoading$.next(true);
        const [customerListConnectedWithBakery2BClient, listGridCustomers] =
          await bakery2bService.globHelpers.streamToPromise(
            bakery2bService.sub.listCustomerConnectedWithBakery2bClient().pipe(
              zipWith(customerService.sub.listGridCustomers()),
              responseHandler<[ListCustomerConnectedWithBakery2bClientQueryRes, ListCustomersRes]>({
                errorReturnType: [[], []],
              }),
              take(1),
            ),
            () => {
              bakery2bService.pub.listCustomerConnectedWithBakery2bClient({
                listWaCustomerConnectedWithBakery2BClientId: selectedBakery2bClient!.id,
              });
              customerService.pub.listGridCustomers({
                filter: {
                  isActive: true,
                },
                cache: true,
              });
            },
          );
        return { customerListConnectedWithBakery2BClient, listGridCustomers };
      }),
      map(({ customerListConnectedWithBakery2BClient, listGridCustomers }) => {
        const updatedState = structuredClone(this.stream$.getValue());
        updatedState.listGridCustomers = listGridCustomers.map((el) => ({
          ...el,
          label: el.internalOrFullName || '',
        }));
        updatedState.customerListConnectedWithBakery2BClient =
          customerListConnectedWithBakery2BClient;
        this.connectedCustomersLoading$.next(false);
        return updatedState;
      }),
    );
  }
  private addCustomerConnectedWithBakery2BClient(): Observable<IBakery2bClientsState> {
    return this.actionListener('addCustomerConnectedWithBakery2BClient').pipe(
      filter(
        ({ customerConnectedWithBakery2BClientProcessedId, selectedBakery2bClient }) =>
          !!(customerConnectedWithBakery2BClientProcessedId && selectedBakery2bClient),
      ),
      tap(({ customerConnectedWithBakery2BClientProcessedId, selectedBakery2bClient }) => {
        this.connectedCustomersLoading$.next(true);
        bakery2bService.pub.addCustomerConnectedWithBakery2BClient({
          customerData: {
            email: selectedBakery2bClient!.email,
            customerId: customerConnectedWithBakery2BClientProcessedId!,
          },
        });
      }),
      switchMap(() => {
        return bakery2bService.sub.addCustomerConnectedWithBakery2BClient().pipe(
          responseHandler<AddCustomerConnectedWithBakery2BClientMutationRes | null>({
            errorReturnType: null,
          }),
          tap(() => this.connectedCustomersLoading$.next(false)),
          filter((res) => !!res),
          map((customer) => {
            const updatedState = structuredClone(this.stream$.getValue());
            updatedState.customerListConnectedWithBakery2BClient.push({
              id: customer.customerId,
              customerNo: customer.customerNo,
              name: customer.name,
            });
            snackbarService.pub.show({
              type: 'success',
              content: t('settings.configuration_saved'),
            });
            return updatedState;
          }),
        );
      }),
    );
  }

  private deleteCustomerConnectedWithBakery2BClient(): Observable<IBakery2bClientsState> {
    return this.actionListener('deleteCustomerConnectedWithBakery2BClient').pipe(
      filter(
        ({ customerConnectedWithBakery2BClientProcessedId, selectedBakery2bClient }) =>
          !!(customerConnectedWithBakery2BClientProcessedId && selectedBakery2bClient),
      ),
      tap(({ customerConnectedWithBakery2BClientProcessedId, selectedBakery2bClient }) => {
        this.connectedCustomersLoading$.next(true);
        bakery2bService.pub.deleteCustomerConnectedWithBakery2BClient({
          customerData: {
            email: selectedBakery2bClient!.email,
            customerId: customerConnectedWithBakery2BClientProcessedId!,
          },
        });
      }),
      switchMap(() => {
        return bakery2bService.sub.deleteCustomerConnectedWithBakery2BClient().pipe(
          responseHandler<DeleteCustomerConnectedWithBakery2BClientMutationRes>({
            errorReturnType: { status: C_Delete_Operation_Status.DOS_RECORD_NOT_DELETED },
          }),
          tap(() => this.connectedCustomersLoading$.next(false)),
          filter(({ status }) => status === C_Delete_Operation_Status.DOS_RECORD_DELETED),
          map(() => {
            const updatedState = structuredClone(this.stream$.getValue());
            updatedState.customerListConnectedWithBakery2BClient =
              updatedState.customerListConnectedWithBakery2BClient.filter(
                (el) => el.id !== updatedState.customerConnectedWithBakery2BClientProcessedId,
              );
            updatedState.customerConnectedWithBakery2BClientProcessedId = null;
            snackbarService.pub.show({
              type: 'success',
              content: t('settings.configuration_saved'),
            });
            return updatedState;
          }),
        );
      }),
    );
  }

  private updateState(): Observable<IBakery2bClientsState> {
    return this.actionListener(['search', 'sort', 'insertBakery2bClientList']);
  }
}

class Bakery2bClientsState extends State<IBakery2bClientsState> {
  pub = new PubImpl(this.stream$);
  sub = new SubImpl(this.stream$);
}

export const bakery2bClientsState = new Bakery2bClientsState(
  structuredClone(initBakery2bClientsState),
);

export interface IBakery2bClientsState extends Pick<ITabState, 'listBakery2bClient'> {
  action:
    | 'init'
    | 'internalUpdate'
    | 'bakery2bClientList'
    | 'selectBakery2bClient'
    | 'saveBakery2bClient'
    | 'deactivateBakery2bClient'
    | 'activateBakery2bClient'
    | 'reinviteBakery2bClient'
    | 'deleteBakery2bClient'
    | 'customerListConnectedWithBakery2BClient'
    | 'addCustomerConnectedWithBakery2BClient'
    | 'deleteCustomerConnectedWithBakery2BClient'
    | 'insertBakery2bClientList'
    | 'sort'
    | 'search'
    | undefined;
  allBakery2bClientListLength: number;
  listBakery2bClientSortModel: GridSortModel;
  listGridCustomers: Array<ExtendedSelectOption>;
  search: string;
  selectedBakery2bClient: IBakery2bClientsState['listBakery2bClient'][number] | null;
  customerListConnectedWithBakery2BClient: ListCustomerConnectedWithBakery2bClientQueryRes;
  customerConnectedWithBakery2BClientProcessedId:
    | ListCustomerConnectedWithBakery2bClientQueryRes[number]['id']
    | null;
}

export interface ExtendedSelectOption extends ISelectOption {
  customerNo?: string | null;
  internalOrFullName?: string | null;
}
